import { useState, useCallback, useMemo } from 'react';

import { PaginationQuery } from '@api';

const initialPaginationQuery = new PaginationQuery({
	descendingOrder: true,
	currentPage: 1,
	pageSize: 9,
});

interface PaginationController {
	paginationQuery: PaginationQuery;
	changePaginationQuery: (n: number) => void;
}

interface UsePaginationQuery {
	initialCurrentPage?: number;
	onPageChange?: (newCurrentPage: number) => void;
}

function usePagination(props?: UsePaginationQuery): PaginationController {
	const { onPageChange, initialCurrentPage } = props;
	const initPaginationQuery = useMemo(() => new PaginationQuery({
		...initialPaginationQuery,
		currentPage: initialCurrentPage || initialPaginationQuery.currentPage,
	}), [initialCurrentPage]);
	const [paginationQuery, setPaginationQuery] = useState<PaginationQuery>(initPaginationQuery);

	const changePaginationQuery = useCallback((newCurrentPage: number) => {
		const newPaginationQuery = new PaginationQuery({
			...initialPaginationQuery,
			currentPage: newCurrentPage,
		});

		if (onPageChange) {
			onPageChange(newCurrentPage);
		}

		setPaginationQuery(newPaginationQuery);
	}, [setPaginationQuery, onPageChange]);

	const paginationController = useMemo((): PaginationController => ({
		paginationQuery,
		changePaginationQuery,
	}), [paginationQuery, changePaginationQuery]);

	return paginationController;
}

export {usePagination};